import React, { useEffect, useMemo, useRef, useState } from "react";
import usePagination, { paginationRangeLabel, paginationSx } from "../../../hooks/usePagination";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllTeachers, updateTeacher } from "../../../redux/teacherRelated/teacherHandle";
import { deleteUser } from "../../../redux/userRelated/userHandle";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    Container,
    Fade,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    Switch,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    Pagination,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import PostAddIcon from "@mui/icons-material/PostAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import { getAllSclasses, getSubjectList, updateSclass } from "../../../redux/sclassRelated/sclassHandle";
import {
    removeTeachSubject,
    updateTeachSubject,
} from "../../../redux/teacherRelated/teacherHandle";
import HomeroomAssignSubjectsFields from "../../../components/admin/HomeroomAssignSubjectsFields";
import {
    getTeacherSubjectIdsInClass,
    HOMEROOM_ASSIGN_SUBJECTS_MSG,
    syncTeacherSubjectsForClass,
} from "../../../utils/homeroomAssignSubjects";
import Popup from "../../../components/Popup";
import ConfirmDeleteDialog from "../../../components/ConfirmDeleteDialog";
import AddStaffRosterDialog from "../../../components/admin/AddStaffRosterDialog";
import {
    getStaffRoleLabel,
    getStaffRoleOptions,
    isHomeroomTeacherRole,
    normalizeStaffRole,
} from "../../../utils/staffRoles";
import { useHomeroomTeacherTerm } from "../../../hooks/useHomeroomTeacherTerm";
import { CLASS_TEACHER_REQUIRES_SUBJECT_MSG } from "../../../utils/classTeacherUtils";

const headCells = [
    { id: "portal", label: "Portal login", width: "10%" },
    { id: "role", label: "Role", width: "12%" },
    { id: "name", label: "Name", width: "12%" },
    { id: "loginEmail", label: "Login email", width: "16%" },
    { id: "phone", label: "Phone", width: "10%" },
    { id: "tscNumber", label: "TCM", width: "10%" },
    { id: "tscVerification", label: "TSC verify", width: "10%" },
    { id: "contractType", label: "Contract", width: "10%" },
    { id: "payroll", label: "Salary / payroll", width: "14%" },
    { id: "leave", label: "Leave", width: "10%" },
    { id: "lastAttendance", label: "Last attendance", width: "14%" },
    { id: "subjectClass", label: "Subject / class", width: "10%" },
    { id: "actions", label: "Set role", align: "right", width: "14%" },
];

const stickyActionCellSx = {
    position: "sticky",
    right: 0,
    zIndex: 1,
    bgcolor: "background.paper",
    whiteSpace: "nowrap",
    boxShadow: (t) => `-6px 0 12px ${alpha(t.palette.common.black, 0.06)}`,
};

const stickyActionHeadSx = {
    ...stickyActionCellSx,
    zIndex: 3,
    bgcolor: (t) => t.palette.background.paper,
};

function TableSkeleton() {
    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        {headCells.map((c) => (
                            <TableCell key={c.id} sx={{ fontWeight: 600 }}>
                                {c.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <TableRow key={i}>
                            <TableCell colSpan={headCells.length}>
                                <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1 }} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function formatMoney(n) {
    if (n === null || n === undefined || n === "") return null;
    const num = Number(n);
    if (!Number.isFinite(num)) return null;
    return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function formatLastAttendance(row) {
    const a = row.lastAttendanceRow;
    if (!a) return "—";
    const d = a.date ? new Date(a.date) : null;
    const dateStr = d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : "—";
    const st = a.status?.trim() || "—";
    return `${dateStr} · ${st}`;
}

function RowActions({ row, onDeleteTeacher, onChangeRole, onAssignHomeroom, onTogglePortal }) {
    const navigate = useNavigate();
    const [menuAnchor, setMenuAnchor] = useState(null);

    return (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
            <Tooltip title="View profile & HR" arrow>
                <IconButton
                    size="small"
                    color="primary"
                    onClick={() => navigate(`/Admin/teachers/teacher/${row.id}`)}
                    sx={{
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                        "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.16) },
                    }}
                >
                    <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="More" arrow>
                <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                    <MoreHorizIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                <MenuItem
                    onClick={() => {
                        setMenuAnchor(null);
                        onTogglePortal(row);
                    }}
                >
                    {row.portalEnabled ? "Disable portal login" : "Enable portal login"}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setMenuAnchor(null);
                        onAssignHomeroom(row);
                    }}
                >
                    Assign to a class
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setMenuAnchor(null);
                        onChangeRole(row);
                    }}
                >
                    Change role
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setMenuAnchor(null);
                        navigate(`/Admin/teachers/chooseclass/${row.id}`);
                    }}
                >
                    {row.teachSubjectName ? "Add another subject" : "Assign subjects"}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setMenuAnchor(null);
                        onDeleteTeacher(row);
                    }}
                    sx={{ color: "error.main", gap: 1 }}
                >
                    <DeleteOutlineIcon fontSize="small" />
                    Delete teacher
                </MenuItem>
            </Menu>
        </Stack>
    );
}

const ShowTeachers = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { teachersList, loading, error, response } = useSelector((state) => state.teacher);
    const { sclassesList, subjectsList, subloading: subjectsLoading } = useSelector((state) => state.sclass);
    const { currentUser } = useSelector((state) => state.user);
    const homeroomTerm = useHomeroomTeacherTerm();
    const schoolType = currentUser?.schoolType;
    const staffRoleOptions = React.useMemo(
        () => getStaffRoleOptions(schoolType),
        [schoolType],
    );

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [roleDialog, setRoleDialog] = useState(null);
    const [roleSaving, setRoleSaving] = useState(false);
    const [addStaffOpen, setAddStaffOpen] = useState(false);

    useEffect(() => {
        if (!currentUser?._id) return;
        dispatch(getAllTeachers(currentUser._id));
        dispatch(getAllSclasses(currentUser._id, "Sclass"));
    }, [currentUser?._id, dispatch]);

    const rows = useMemo(() => {
        if (!Array.isArray(teachersList) || teachersList.length === 0) return [];
        return teachersList.map((teacher) => {
            const last = Array.isArray(teacher.attendance) && teacher.attendance.length > 0 ? teacher.attendance[0] : null;
            const allocated = teacher.annualLeaveAllocated;
            const used = teacher.annualLeaveUsed ?? 0;
            const salary = formatMoney(teacher.salaryAmount);
            const payroll = teacher.payrollRef?.trim();
            const payrollLabel =
                salary != null && payroll
                    ? `${salary} · ${payroll}`
                    : salary != null
                      ? String(salary)
                      : payroll || "—";
            const staffRole = normalizeStaffRole(teacher.staffRole ?? teacher.role);
            return {
                id: teacher._id,
                role: staffRole,
                roleLabel: getStaffRoleLabel(staffRole, schoolType),
                name: teacher.name,
                phone: teacher.phone?.trim() || "—",
                tscNumber: teacher.tscNumber?.trim() || "—",
                tscVerificationStatus: teacher.tscVerificationStatus || "Pending",
                contractType: teacher.contractType?.trim() || "",
                payrollLabel,
                leaveLabel: `${used} / ${allocated != null && allocated !== "" ? allocated : "—"} used`,
                lastAttendanceRow: last,
                teachSubjectName: (() => {
                    const list = Array.isArray(teacher.teachSubjects)
                        ? teacher.teachSubjects
                        : teacher.teachSubject?.subName
                          ? [teacher.teachSubject]
                          : [];
                    return list.map((s) => s.subName).filter(Boolean).join(", ") || "";
                })(),
                teachSclassName: (() => {
                    const list = Array.isArray(teacher.teachSubjects)
                        ? teacher.teachSubjects
                        : teacher.teachSubject?.subName
                          ? [teacher.teachSubject]
                          : [];
                    const classes = [
                        ...new Set(
                            list
                                .map((s) => s.sclassName?.sclassName)
                                .filter(Boolean)
                        ),
                    ];
                    if (classes.length === 0) return teacher.teachSclass?.sclassName || "—";
                    return classes.join(", ");
                })(),
                teachSclassID: teacher.teachSclass?._id,
                formTeacherOfName: (() => {
                    const homerooms = Array.isArray(teacher.classTeacherOf) ? teacher.classTeacherOf : [];
                    return homerooms.map((c) => c.sclassName).filter(Boolean).join(", ");
                })(),
                profileCompleted: Boolean(teacher.profileCompleted),
                email: teacher.email || "",
                emailIsTemporary: Boolean(teacher.emailIsTemporary),
                portalEnabled: teacher.portalEnabled !== false,
            };
        });
    }, [teachersList, schoolType]);

    const [tscFilter, setTscFilter] = useState("");

    const filteredRows = useMemo(() => {
        if (!tscFilter) return rows;
        return rows.filter((r) => {
            const status = r.tscVerificationStatus || "Pending";
            if (tscFilter === "Pending") return !status || status === "Pending";
            return status === tscFilter;
        });
    }, [rows, tscFilter]);

    const pageSize = 25;
    const { page, setPage, pageCount, paginatedItems, totalItems } = usePagination(filteredRows, pageSize);

    const [deleteDialog, setDeleteDialog] = useState(null);
    const [homeroomDialog, setHomeroomDialog] = useState(null);
    const [homeroomSaving, setHomeroomSaving] = useState(false);
    const staffTableRef = useRef(null);

    const classOptions = useMemo(() => {
        if (!Array.isArray(sclassesList)) return [];
        return sclassesList
            .map((c) => ({
                id: c._id || c.id,
                name: c.sclassName || "—",
                currentTeacherName: c.classTeacher?.name || null,
            }))
            .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    }, [sclassesList]);

    const scrollToRoleAssign = () => {
        staffTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const refresh = () => dispatch(getAllTeachers(currentUser._id));

    const toggleTeacherPortal = async (row) => {
        const next = !row.portalEnabled;
        const outcome = await dispatch(updateTeacher(row.id, { portalEnabled: next }));
        if (outcome?.success === false) {
            setMessage(outcome?.message || "Could not update portal access.");
            setShowPopup(true);
            return;
        }
        refresh();
        setMessage(
            next
                ? `${row.name} can sign in to the staff portal again.`
                : `${row.name} can no longer sign in. Portal access is off.`,
        );
        setShowPopup(true);
    };

    const closeDeleteDialog = () => setDeleteDialog(null);

    const openDeleteTeacherDialog = (row) => {
        setDeleteDialog({
            title: `Delete teacher: ${row.name}`,
            description:
                "This will permanently delete this staff member, their portal account, attendance log, and subject assignments.",
            confirmWord: row.name,
            onConfirm: async () => {
                const outcome = await dispatch(deleteUser(row.id, "Teacher"));
                if (outcome?.success) {
                    refresh();
                    return;
                }
                setMessage("Could not delete the teacher.");
                setShowPopup(true);
            },
        });
    };

    const teacherHomeroomClassId = (row) => {
        const t = teachersList?.find((x) => (x._id || x.id) === row.id);
        const homerooms = Array.isArray(t?.classTeacherOf) ? t.classTeacherOf : [];
        return homerooms[0]?._id || homerooms[0]?.id || "";
    };

    const classSubjects = useMemo(
        () => (Array.isArray(subjectsList) ? subjectsList : []),
        [subjectsList],
    );

    const loadHomeroomClassSubjects = (classId) => {
        if (classId) dispatch(getSubjectList(classId, "ClassSubjects"));
    };

    const openHomeroomAssignDialog = (row) => {
        const teacher = teachersList?.find((t) => (t._id || t.id) === row.id);
        const currentClassId = teacherHomeroomClassId(row);
        const classId =
            classOptions.some((c) => c.id === currentClassId) ? currentClassId : classOptions[0]?.id || "";
        const selectedSubjectIds = classId ? getTeacherSubjectIdsInClass(teacher, classId) : [];
        if (classId) loadHomeroomClassSubjects(classId);
        setHomeroomDialog({
            row,
            classId,
            selectedSubjectIds,
            setRoleToo: !isHomeroomTeacherRole(row.role),
        });
    };

    const closeHomeroomDialog = () => setHomeroomDialog(null);

    const handleHomeroomClassChange = (classId) => {
        const teacher = teachersList?.find((t) => (t._id || t.id) === homeroomDialog?.row?.id);
        loadHomeroomClassSubjects(classId);
        setHomeroomDialog((prev) =>
            prev
                ? {
                      ...prev,
                      classId,
                      selectedSubjectIds: teacher ? getTeacherSubjectIdsInClass(teacher, classId) : [],
                  }
                : prev,
        );
    };

    const toggleHomeroomSubject = (subjectId) => {
        setHomeroomDialog((prev) => {
            if (!prev) return prev;
            const set = new Set(prev.selectedSubjectIds || []);
            if (set.has(subjectId)) set.delete(subjectId);
            else set.add(subjectId);
            return { ...prev, selectedSubjectIds: [...set] };
        });
    };

    const saveHomeroomAssign = async () => {
        if (!homeroomDialog?.row?.id || !homeroomDialog?.classId) return;
        const teacher = teachersList?.find((t) => (t._id || t.id) === homeroomDialog.row.id);
        if (!homeroomDialog.selectedSubjectIds?.length) {
            setMessage(HOMEROOM_ASSIGN_SUBJECTS_MSG);
            setShowPopup(true);
            return;
        }
        setHomeroomSaving(true);
        const teacherId = homeroomDialog.row.id;
        const classId = homeroomDialog.classId;
        const classLabel = classOptions.find((c) => c.id === classId)?.name || "class";

        const syncOutcome = await syncTeacherSubjectsForClass(dispatch, {
            teacherId,
            classId,
            selectedSubjectIds: homeroomDialog.selectedSubjectIds,
            teacher,
            updateTeachSubject,
            removeTeachSubject,
        });
        if (!syncOutcome.success) {
            setHomeroomSaving(false);
            setMessage(syncOutcome.message || CLASS_TEACHER_REQUIRES_SUBJECT_MSG);
            setShowPopup(true);
            return;
        }

        const classOutcome = await dispatch(updateSclass(classId, { classTeacherId: teacherId }));
        let teacherOutcome = { success: true };
        if (homeroomDialog.setRoleToo) {
            teacherOutcome = await dispatch(updateTeacher(teacherId, { role: homeroomTerm.roleValue }));
        }
        setHomeroomSaving(false);
        setHomeroomDialog(null);
        dispatch(getAllSclasses(currentUser._id, "Sclass"));
        refresh();

        if (classOutcome?.success === false || teacherOutcome?.success === false) {
            setMessage(
                classOutcome?.message ||
                    teacherOutcome?.message ||
                    "Could not assign class teacher.",
            );
            setShowPopup(true);
            return;
        }

        setMessage(
            `${homeroomDialog.row.name} is now ${homeroomTerm.assignOnClass} of ${classLabel} (${syncOutcome.subjectCount} subject(s) assigned)${
                homeroomDialog.setRoleToo ? ` · ${homeroomTerm.roleTitleCaps} role set` : ""
            }.`,
        );
        setShowPopup(true);
    };

    const openRoleDialog = (row) => {
        const normalized = normalizeStaffRole(row.role);
        const role = isHomeroomTeacherRole(normalized)
            ? homeroomTerm.roleValue
            : normalized;
        const currentClassId = teacherHomeroomClassId(row);
        const classId = isHomeroomTeacherRole(role)
            ? classOptions.some((c) => c.id === currentClassId)
                ? currentClassId
                : classOptions[0]?.id || ""
            : "";
        const teacher = teachersList?.find((t) => (t._id || t.id) === row.id);
        const selectedSubjectIds =
            isHomeroomTeacherRole(role) && classId ? getTeacherSubjectIdsInClass(teacher, classId) : [];
        if (isHomeroomTeacherRole(role) && classId) loadHomeroomClassSubjects(classId);
        setRoleDialog({
            row,
            role,
            classId,
            selectedSubjectIds,
        });
    };

    const handleRoleClassChange = (classId) => {
        const teacher = teachersList?.find((t) => (t._id || t.id) === roleDialog?.row?.id);
        loadHomeroomClassSubjects(classId);
        setRoleDialog((prev) =>
            prev
                ? {
                      ...prev,
                      classId,
                      selectedSubjectIds: teacher ? getTeacherSubjectIdsInClass(teacher, classId) : [],
                  }
                : prev,
        );
    };

    const toggleRoleSubject = (subjectId) => {
        setRoleDialog((prev) => {
            if (!prev) return prev;
            const set = new Set(prev.selectedSubjectIds || []);
            if (set.has(subjectId)) set.delete(subjectId);
            else set.add(subjectId);
            return { ...prev, selectedSubjectIds: [...set] };
        });
    };

    const closeRoleDialog = () => setRoleDialog(null);

    const saveRole = async () => {
        if (!roleDialog?.row?.id || !roleDialog?.role) return;
        if (isHomeroomTeacherRole(roleDialog.role) && !roleDialog.classId) {
            setMessage(`Choose which class this person is ${homeroomTerm.assignOnClass} of.`);
            setShowPopup(true);
            return;
        }
        if (isHomeroomTeacherRole(roleDialog.role) && roleDialog.classId) {
            if (!roleDialog.selectedSubjectIds?.length) {
                setMessage(HOMEROOM_ASSIGN_SUBJECTS_MSG);
                setShowPopup(true);
                return;
            }
        }
        setRoleSaving(true);
        const selected = normalizeStaffRole(roleDialog.role);
        const classId = roleDialog.classId;
        const classLabel = classOptions.find((c) => c.id === classId)?.name;
        const teacher = teachersList?.find((t) => (t._id || t.id) === roleDialog.row.id);

        let syncOutcome = { success: true };
        if (isHomeroomTeacherRole(selected) && classId) {
            syncOutcome = await syncTeacherSubjectsForClass(dispatch, {
                teacherId: roleDialog.row.id,
                classId,
                selectedSubjectIds: roleDialog.selectedSubjectIds,
                teacher,
                updateTeachSubject,
                removeTeachSubject,
            });
            if (!syncOutcome.success) {
                setRoleSaving(false);
                setMessage(syncOutcome.message || CLASS_TEACHER_REQUIRES_SUBJECT_MSG);
                setShowPopup(true);
                return;
            }
        }

        const teacherOutcome = await dispatch(updateTeacher(roleDialog.row.id, { role: selected }));
        let classOutcome = { success: true };
        if (isHomeroomTeacherRole(selected) && classId) {
            classOutcome = await dispatch(
                updateSclass(classId, { classTeacherId: roleDialog.row.id }),
            );
            dispatch(getAllSclasses(currentUser._id, "Sclass"));
        }

        setRoleSaving(false);
        setRoleDialog(null);
        refresh();

        if (teacherOutcome?.success === false || classOutcome?.success === false) {
            setMessage(
                classOutcome?.message ||
                    teacherOutcome?.message ||
                    "Could not save staff role or class assignment.",
            );
            setShowPopup(true);
            return;
        }

        setMessage(
            classLabel && isHomeroomTeacherRole(selected)
                ? `Role set to ${getStaffRoleLabel(selected, schoolType)} for ${classLabel}.`
                : `Role updated to ${getStaffRoleLabel(selected, schoolType)}.`,
        );
        setShowPopup(true);
    };

    const openDeleteAllTeachersDialog = () => {
        setDeleteDialog({
            title: "Delete all teachers",
            description: "This will permanently delete every teacher and staff account for this school. This cannot be undone.",
            confirmWord: "DELETE ALL TEACHERS",
            onConfirm: async () => {
                const outcome = await dispatch(deleteUser(currentUser._id, "Teachers"));
                if (outcome?.success) {
                    refresh();
                    return;
                }
                setMessage("Could not delete all teachers.");
                setShowPopup(true);
            },
        });
    };

    const handleConfirmDelete = async () => {
        if (deleteDialog?.onConfirm) {
            await deleteDialog.onConfirm();
        }
        closeDeleteDialog();
    };

    const showEmpty = !loading && !error && filteredRows.length === 0;
    const showTable = !loading && !error && filteredRows.length > 0;

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100%",
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 28%)`
                        : alpha(t.palette.common.black, 0.2),
                pb: 6,
            }}
        >
            <Container maxWidth="lg" sx={{ pt: { xs: 2, sm: 3 }, pb: 2 }}>
                <Fade in timeout={380}>
                    <Stack spacing={3}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2.5, sm: 3 },
                                borderRadius: 3,
                                color: "common.white",
                                background: (t) =>
                                    `linear-gradient(120deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 55%, ${alpha(
                                        t.palette.primary.light,
                                        0.92
                                    )} 100%)`,
                                boxShadow: (t) => `0 12px 40px ${alpha(t.palette.primary.main, 0.28)}`,
                            }}
                        >
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                            >
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: alpha("#fff", 0.15),
                                        }}
                                    >
                                        <Groups2OutlinedIcon sx={{ fontSize: 32 }} />
                                    </Box>
                                    <Stack spacing={0.75}>
                                        <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1.2 }}>
                                            Staff
                                        </Typography>
                                        <Typography variant="h4" component="h1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                                            School staff
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9, maxWidth: 620 }}>
                                            Teachers, bursar, heads of department, and librarians. Use{" "}
                                            <strong>Assign roles</strong> below to set each person&apos;s job (e.g. class
                                            teacher on primary). The bursar records fees; you keep finance oversight here.
                                        </Typography>
                                    </Stack>
                                </Stack>
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1.5}
                                    alignItems="stretch"
                                    flexWrap="wrap"
                                    useFlexGap
                                    sx={{ flexShrink: 0 }}
                                >
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<PostAddIcon />}
                                        onClick={() => navigate("/Admin/staff/role")}
                                        sx={{
                                            bgcolor: "common.white",
                                            color: "primary.main",
                                            fontWeight: 600,
                                            px: 2.5,
                                            py: 1.25,
                                            borderRadius: 2,
                                            boxShadow: (t) => `0 2px 12px ${alpha(t.palette.common.black, 0.15)}`,
                                            "&:hover": {
                                                bgcolor: (t) => alpha(t.palette.common.white, 0.92),
                                                boxShadow: (t) => `0 4px 20px ${alpha(t.palette.common.black, 0.2)}`,
                                            },
                                        }}
                                    >
                                        Add staff
                                    </Button>
                                    <Tooltip title="Upload a CSV to add multiple staff members and assign their roles" arrow>
                                        <Button
                                            variant="outlined"
                                            size="large"
                                            startIcon={<CloudUploadOutlinedIcon />}
                                            onClick={() => setAddStaffOpen(true)}
                                            sx={{
                                                borderColor: alpha("#fff", 0.7),
                                                color: "common.white",
                                                borderRadius: 2,
                                                borderWidth: 2,
                                                fontWeight: 600,
                                                "&:hover": {
                                                    borderColor: "common.white",
                                                },
                                            }}
                                        >
                                            Bulk Upload Staff
                                        </Button>
                                    </Tooltip>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<AssignmentIndOutlinedIcon />}
                                        onClick={scrollToRoleAssign}
                                        sx={{
                                            bgcolor: (t) => t.palette.warning.main,
                                            color: (t) => t.palette.warning.contrastText,
                                            fontWeight: 700,
                                            px: 2.5,
                                            py: 1.25,
                                            borderRadius: 2,
                                            boxShadow: (t) => `0 2px 12px ${alpha(t.palette.common.black, 0.2)}`,
                                            "&:hover": {
                                                bgcolor: (t) => t.palette.warning.dark,
                                                boxShadow: (t) => `0 4px 20px ${alpha(t.palette.common.black, 0.25)}`,
                                            },
                                        }}
                                    >
                                        Assign roles
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        startIcon={<VerifiedUserOutlinedIcon />}
                                        onClick={() => navigate("/Admin/teachers/management")}
                                        sx={{
                                            borderColor: alpha("#fff", 0.7),
                                            color: "common.white",
                                            borderRadius: 2,
                                            borderWidth: 2,
                                            fontWeight: 600,
                                            "&:hover": {
                                                borderColor: "common.white",
                                                borderWidth: 2,
                                                bgcolor: alpha("#fff", 0.1),
                                            },
                                        }}
                                    >
                                        HR dashboard
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        startIcon={<DeleteOutlineIcon />}
                                        onClick={openDeleteAllTeachersDialog}
                                        sx={{
                                            borderColor: alpha("#fff", 0.7),
                                            color: "common.white",
                                            borderRadius: 2,
                                            borderWidth: 2,
                                            fontWeight: 600,
                                            "&:hover": {
                                                borderColor: "common.white",
                                                borderWidth: 2,
                                                bgcolor: alpha("#fff", 0.1),
                                            },
                                        }}
                                    >
                                        Delete all
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>

                        {error && (
                            <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                                {error?.message || "Could not load teachers."}
                            </Alert>
                        )}

                        {!error && (
                            <Paper
                                ref={staffTableRef}
                                id="staff-role-table"
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                    boxShadow: (t) => `0 4px 24px ${alpha(t.palette.common.black, 0.06)}`,
                                    scrollMarginTop: 88,
                                }}
                            >
                                {loading && (
                                    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <TableSkeleton />
                                    </Box>
                                )}

                                {showEmpty && (
                                    <Box sx={{ py: { xs: 6, sm: 8 }, px: 2, textAlign: "center" }}>
                                        <Typography variant="h6" gutterBottom fontWeight={600}>
                                            {rows.length > 0 && tscFilter ? "No staff match this TSC filter" : "No staff yet"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: "auto", mb: 3 }}>
                                            {rows.length > 0 && tscFilter
                                                ? "Try clearing the TSC status filter to see all staff."
                                                : typeof response === "string"
                                                  ? response
                                                  : "Add staff with temporary login credentials. Teachers and HODs can be linked to subjects; the bursar records finance in their portal while you keep oversight here."}
                                        </Typography>
                                        {rows.length > 0 && tscFilter ? (
                                            <Button variant="outlined" onClick={() => setTscFilter("")}>
                                                Clear filter
                                            </Button>
                                        ) : (
                                            <Button variant="contained" size="large" onClick={() => navigate("/Admin/staff/role")}>
                                                Add your first staff member
                                            </Button>
                                        )}
                                    </Box>
                                )}

                                {showTable && (
                                    <>
                                        <Box
                                            sx={{
                                                px: { xs: 2, sm: 2.5 },
                                                py: 2,
                                                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                                bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
                                            }}
                                        >
                                            <Stack
                                                direction={{ xs: "column", sm: "row" }}
                                                spacing={1.5}
                                                alignItems={{ xs: "stretch", sm: "center" }}
                                                justifyContent="space-between"
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    {paginationRangeLabel(page, pageSize, totalItems, "staff")}
                                                    {tscFilter ? ` · TSC filter: ${tscFilter}` : ""}
                                                </Typography>
                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                    <FormControl size="small" sx={{ minWidth: 160 }}>
                                                        <InputLabel>TSC status</InputLabel>
                                                        <Select
                                                            label="TSC status"
                                                            value={tscFilter}
                                                            onChange={(e) => {
                                                                setTscFilter(e.target.value);
                                                                setPage(1);
                                                            }}
                                                        >
                                                            <MenuItem value="">All</MenuItem>
                                                            <MenuItem value="Verified">Verified</MenuItem>
                                                            <MenuItem value="Pending">Pending</MenuItem>
                                                            <MenuItem value="Rejected">Rejected</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                    <Typography variant="body2" color="text.primary" fontWeight={600}>
                                                        Assign roles: click <strong>Set role</strong> (scroll right).
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </Box>
                                        <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        {headCells.map((c) => (
                                                            <TableCell
                                                                key={c.id}
                                                                align={c.align}
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                                                                    whiteSpace: "nowrap",
                                                                    ...(c.id === "actions" ? stickyActionHeadSx : {}),
                                                                }}
                                                            >
                                                                {c.label}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {paginatedItems.map((row) => (
                                                        <TableRow key={row.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                                                            <TableCell>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Switch
                                                                        size="small"
                                                                        checked={row.portalEnabled}
                                                                        onChange={() => toggleTeacherPortal(row)}
                                                                        inputProps={{
                                                                            "aria-label": `Staff portal for ${row.name}`,
                                                                        }}
                                                                    />
                                                                    <Chip
                                                                        size="small"
                                                                        label={row.portalEnabled ? "On" : "Off"}
                                                                        color={row.portalEnabled ? "success" : "warning"}
                                                                        variant={row.portalEnabled ? "filled" : "outlined"}
                                                                        sx={{ fontWeight: 600 }}
                                                                    />
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip size="small" label={row.roleLabel} variant="outlined" sx={{ fontWeight: 600 }} />
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>
                                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                                    <span>{row.name}</span>
                                                                    {!row.profileCompleted && (
                                                                        <Chip label="Incomplete profile" size="small" color="warning" variant="outlined" />
                                                                    )}
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell sx={{ maxWidth: 200 }}>
                                                                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                                                                    {row.email || "—"}
                                                                </Typography>
                                                                {row.emailIsTemporary && (
                                                                    <Chip
                                                                        label="Temporary login"
                                                                        size="small"
                                                                        color="warning"
                                                                        variant="outlined"
                                                                        sx={{ mt: 0.5 }}
                                                                    />
                                                                )}
                                                            </TableCell>
                                                            <TableCell>{row.phone}</TableCell>
                                                            <TableCell>{row.tscNumber}</TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    size="small"
                                                                    label={row.tscVerificationStatus || "Pending"}
                                                                    color={
                                                                        row.tscVerificationStatus === "Verified"
                                                                            ? "success"
                                                                            : row.tscVerificationStatus === "Rejected"
                                                                              ? "error"
                                                                              : "warning"
                                                                    }
                                                                    variant="outlined"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                {row.contractType ? (
                                                                    <Chip size="small" label={row.contractType} variant="outlined" />
                                                                ) : (
                                                                    "—"
                                                                )}
                                                            </TableCell>
                                                            <TableCell sx={{ maxWidth: 200 }}>{row.payrollLabel}</TableCell>
                                                            <TableCell>{row.leaveLabel}</TableCell>
                                                            <TableCell sx={{ maxWidth: 220 }}>{formatLastAttendance(row)}</TableCell>
                                                            <TableCell sx={{ maxWidth: 160 }}>
                                                                <Typography variant="body2" noWrap title={row.teachSubjectName || ""}>
                                                                    {row.teachSubjectName || (
                                                                        <Button
                                                                            size="small"
                                                                            variant="text"
                                                                            onClick={() =>
                                                                                navigate(
                                                                                    `/Admin/teachers/chooseclass/${row.id}`
                                                                                )
                                                                            }
                                                                        >
                                                                            Assign
                                                                        </Button>
                                                                    )}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                                                    {row.teachSclassName}
                                                                </Typography>
                                                                {row.formTeacherOfName ? (
                                                                    <Chip
                                                                        size="small"
                                                                        color="primary"
                                                                        variant="outlined"
                                                                        label={`${homeroomTerm.chipPrefix}: ${row.formTeacherOfName}`}
                                                                        sx={{ mt: 0.5, maxWidth: "100%" }}
                                                                    />
                                                                ) : null}
                                                            </TableCell>
                                                            <TableCell align="right" sx={stickyActionCellSx}>
                                                                <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        color="success"
                                                                        startIcon={<ClassOutlinedIcon />}
                                                                        onClick={() => openHomeroomAssignDialog(row)}
                                                                        sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                                                                    >
                                                                        Assign class
                                                                    </Button>
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        startIcon={<AssignmentIndOutlinedIcon />}
                                                                        onClick={() => openRoleDialog(row)}
                                                                        sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                                                                    >
                                                                        Set role
                                                                    </Button>
                                                                    <RowActions
                                                                        row={row}
                                                                        onDeleteTeacher={openDeleteTeacherDialog}
                                                                        onChangeRole={openRoleDialog}
                                                                        onAssignHomeroom={openHomeroomAssignDialog}
                                                                        onTogglePortal={toggleTeacherPortal}
                                                                    />
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        {pageCount > 1 && (
                                            <Pagination
                                                count={pageCount}
                                                page={page}
                                                onChange={(_, v) => setPage(v)}
                                                color="primary"
                                                sx={paginationSx}
                                            />
                                        )}
                                    </>
                                )}
                            </Paper>
                        )}
                    </Stack>
                </Fade>
            </Container>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
            <ConfirmDeleteDialog
                open={Boolean(deleteDialog)}
                onClose={closeDeleteDialog}
                onConfirm={handleConfirmDelete}
                title={deleteDialog?.title ?? ""}
                description={deleteDialog?.description ?? ""}
                confirmWord={deleteDialog?.confirmWord ?? ""}
            />
            <AddStaffRosterDialog
                open={addStaffOpen}
                onClose={() => setAddStaffOpen(false)}
                onStaffAdded={refresh}
                schoolId={currentUser?.schoolId || currentUser?._id}
            />
            <Dialog open={Boolean(roleDialog)} onClose={closeRoleDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Assign role: {roleDialog?.row?.name || ""}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Choose a role. For <strong>{homeroomTerm.roleTitleCaps}</strong>, pick the homeroom class and at
                        least one subject they will teach there.
                    </Typography>
                    <Stack spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel>Staff role</InputLabel>
                            <Select
                                label="Staff role"
                                value={roleDialog?.role || ""}
                                onChange={(e) => {
                                    const role = normalizeStaffRole(e.target.value);
                                    const classId =
                                        isHomeroomTeacherRole(role) && classOptions[0]?.id
                                            ? roleDialog?.classId || classOptions[0].id
                                            : "";
                                    const teacher = teachersList?.find(
                                        (t) => (t._id || t.id) === roleDialog?.row?.id,
                                    );
                                    if (isHomeroomTeacherRole(role) && classId) {
                                        loadHomeroomClassSubjects(classId);
                                    }
                                    setRoleDialog((prev) =>
                                        prev
                                            ? {
                                                  ...prev,
                                                  role,
                                                  classId: isHomeroomTeacherRole(role) ? classId : "",
                                                  selectedSubjectIds:
                                                      isHomeroomTeacherRole(role) && classId && teacher
                                                          ? getTeacherSubjectIdsInClass(teacher, classId)
                                                          : [],
                                              }
                                            : prev,
                                    );
                                }}
                            >
                                {staffRoleOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {isHomeroomTeacherRole(roleDialog?.role) ? (
                            <HomeroomAssignSubjectsFields
                                homeroomLabel={roleDialog?.row?.name}
                                classOptions={classOptions}
                                classId={roleDialog?.classId}
                                onClassChange={handleRoleClassChange}
                                subjects={classSubjects}
                                subjectsLoading={subjectsLoading}
                                selectedSubjectIds={roleDialog?.selectedSubjectIds || []}
                                onToggleSubject={toggleRoleSubject}
                                currentTeacherId={roleDialog?.row?.id || ""}
                            />
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRoleDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={saveRole}
                        disabled={
                            roleSaving ||
                            (isHomeroomTeacherRole(roleDialog?.role) &&
                                (!roleDialog?.classId || !roleDialog?.selectedSubjectIds?.length))
                        }
                    >
                        {roleSaving ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={Boolean(homeroomDialog)} onClose={closeHomeroomDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {homeroomTerm.assignOnClass}: {homeroomDialog?.row?.name || ""}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Choose the homeroom class and which subjects this teacher will teach there. They can then enroll
                        pupils for director verification.
                    </Typography>
                    <Stack spacing={2}>
                        <HomeroomAssignSubjectsFields
                            homeroomLabel={homeroomDialog?.row?.name}
                            classOptions={classOptions}
                            classId={homeroomDialog?.classId}
                            onClassChange={handleHomeroomClassChange}
                            subjects={classSubjects}
                            subjectsLoading={subjectsLoading}
                            selectedSubjectIds={homeroomDialog?.selectedSubjectIds || []}
                            onToggleSubject={toggleHomeroomSubject}
                            currentTeacherId={homeroomDialog?.row?.id || ""}
                        />
                        {homeroomDialog?.setRoleToo ? (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={homeroomDialog.setRoleToo}
                                        onChange={(e) =>
                                            setHomeroomDialog((prev) =>
                                                prev ? { ...prev, setRoleToo: e.target.checked } : prev
                                            )
                                        }
                                    />
                                }
                                label={`Also set staff role to ${homeroomTerm.roleTitleCaps}`}
                            />
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeHomeroomDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={saveHomeroomAssign}
                        disabled={
                            homeroomSaving ||
                            !homeroomDialog?.classId ||
                            !homeroomDialog?.selectedSubjectIds?.length
                        }
                    >
                        {homeroomSaving ? "Saving…" : "Assign"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ShowTeachers;
